import type { QuestionAnswer } from "#types/types.ts";

const RESPONSE_TAG = "workleap_ai_question_response";
const RESPONSE_PATTERN = new RegExp(`^<${RESPONSE_TAG}>([\\s\\S]+)</${RESPONSE_TAG}>$`);
const LEGACY_RESPONSE_PATTERN = /(?:^|\n\n)Q: ([^\n]+)\nR: ([^\n]+)(?=\n\nQ: |$)/g;

// An answer saved as a user message before questions became interrupts: one string, a multiselect's options joined.
interface SavedAnswer {
    question: string;
    answer: string;
}

interface SavedResponsePayload {
    answers: SavedAnswer[];
}

// Reads answers saved as user messages before questions became interrupts, so older conversations still render them.
export function parseInteractiveQuestionResponse(content: string): QuestionAnswer[] | null {
    const trimmedContent = content.trim();
    const taggedMatch = trimmedContent.match(RESPONSE_PATTERN);
    if (taggedMatch) {
        try {
            const payload = JSON.parse(taggedMatch[1]) as Partial<SavedResponsePayload>;
            if (Array.isArray(payload.answers) && payload.answers.length > 0 && payload.answers.every(isSavedAnswer)) {
                return payload.answers.map(toQuestionAnswer);
            }
        } catch {
            return null;
        }
    }

    // Preserve a polished display for answers saved by the original Q:/R: implementation.
    const legacyMatches = Array.from(trimmedContent.matchAll(LEGACY_RESPONSE_PATTERN));
    if (legacyMatches.map(match => match[0]).join("") !== trimmedContent) {
        return null;
    }

    const legacyAnswers = legacyMatches.map(match => toQuestionAnswer({
        question: match[1].trim(),
        answer: match[2].trim()
    }));

    return legacyAnswers.length > 0 ? legacyAnswers : null;
}

const toQuestionAnswer = ({ question, answer }: SavedAnswer): QuestionAnswer => ({ question, answer: [answer] });

function isSavedAnswer(value: unknown): value is SavedAnswer {
    if (!value || typeof value !== "object") { return false; }

    const answer = value as Partial<SavedAnswer>;

    return typeof answer.question === "string" && answer.question.trim().length > 0 &&
        typeof answer.answer === "string" && answer.answer.trim().length > 0;
}
